/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import TicketDTO from '../models/ticket';
import Button from '../components/atoms/Button';
import { setTicketsSelected } from '../store/slices/tickets';
import LoadingLottie from '../components/atoms/LoadingLottie';
import { selectReservation } from '../store/slices/orders';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';
import useNavigateWithQuery from '../hooks/useNavigateWithQuery';
import NumeratedSeats from '../components/NumeratedSeats';
import { useGetAllTicketsQuery } from '../store/apis/tickets';
import { RootState } from '../store';
import { DisplaySeatDTO } from '../models/seat';
import { trackAddToCart } from '../utils/metaPixelTracking';

// Define TicketSelection type (consider moving to a shared types file later)
interface TicketSelection {
  ticket: TicketDTO;
  quantity: number;
  seat_name: string;
  seat_id: string;
}

const SelectSeatsPage = () => {
  const navigate = useNavigateWithQuery();
  const dispatch = useDispatch();
  const location = useLocation();
  const isBackoffice = location.pathname.includes('admin');
  const { event } = useGetSelectedEvent({ slug: !isBackoffice });
  const reservation = useSelector((state: RootState) => state.orders.reservation);
  const { data: eventTickets, isLoading: isLoadingTickets } = useGetAllTicketsQuery(
    { businessId: event?.business_id || '', eventId: event?.id || '', publicSaleAvailable: !isBackoffice },
    { skip: !event },
  );
  const [, setTicketKey] = useState(0);
  const [selectedSeats, setSelectedSeats] = useState<DisplaySeatDTO[]>([]);
  const [selectedTickets, setSelectedTickets] = useState<TicketSelection[]>([]);

  useEffect(() => {
    localStorage.removeItem('formItemsData');
  }, []);

  const [hasCheckedReservation, setHasCheckedReservation] = useState(false);

  useEffect(() => {
    if (event && !hasCheckedReservation) {
      dispatch(selectReservation());
      setHasCheckedReservation(true);
    }
  }, [event, dispatch, hasCheckedReservation]);

  useEffect(() => {
    if (!event) {
      navigate('../../');
    }
  }, [event, navigate]);

  useEffect(() => {
    setTicketKey((prevKey) => prevKey + 1);
  }, [eventTickets]);

  useEffect(() => {
    if (!eventTickets || !reservation?.code || !event?.tickets) {
      return;
    }

    const calculatedTickets = selectedSeats.map((s): TicketSelection | null => {
      const ticket = event.tickets.find((t) => t.id === s.ticket_id);
      if (!ticket) {
        console.error(`Ticket not found for seat ID: ${s.id}, ticket ID: ${s.ticket_id}`);
        return null;
      }
      return {
        ticket,
        quantity: 1,
        seat_name: s.seat_name,
        seat_id: s.id,
      };
    }).filter((t): t is TicketSelection => t !== null);

    setSelectedTickets(calculatedTickets);
  }, [selectedSeats, eventTickets, reservation?.code, event?.tickets]);

  const handleClickNext = async () => {
    if (selectedSeats.length === 0) {
      return '';
    }
    const reserveCode = reservation?.code;
    if (!reserveCode) {
      console.error('no reserve code');
      return '';
    }
    if (!isBackoffice && event) {
      trackAddToCart({
        content_name: event.name,
        content_type: 'product',
        content_ids: Array.from(new Set(selectedTickets.map((ts) => ts.ticket.id))),
        value: selectedTickets.reduce((sum, ts) => sum + ts.ticket.price * ts.quantity, 0),
        num_items: selectedTickets.length,
      });
    }
    dispatch(setTicketsSelected([...selectedTickets]));
    return navigate('../entry-data', { replace: true });
  };

  const handleBack = () => {
    navigate('../../');
  };

  if (isLoadingTickets) {
    return <LoadingLottie />;
  }

  if (!event?.venue) {
    return <div>No venue found</div>;
  }

  return (
    <div className='flex flex-col gap-4'>

      <NumeratedSeats
        event={event}
        businessId={event.business_id}
        selectedSeats={selectedSeats}
        onSelectedSeatsChange={setSelectedSeats}
        tickets={eventTickets ?? []}
      />

      <div className='flex flex-col-reverse md:flex-row gap-4'>
        <Button
          type="button"
          className="w-full"
          variant='secondary'
          onClick={handleBack}
        >
          Volver
        </Button>
        <Button
          type="submit"
          className="w-full"
          onClick={handleClickNext}
          disabled={!selectedSeats.length}
          variant="primary"
        >
          Siguiente
        </Button>
      </div>

    </div>
  );
};

export default SelectSeatsPage;
